import { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import ForceGraph3D from 'react-force-graph-3d';
import SpriteText from 'three-spritetext';
import * as THREE from 'three';
import {
  CalendarRange,
  Info,
  Layers,
  Maximize2,
  Tag,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { berechneGenerationen, tiefenAchse, type Sortierung } from '@/utils/generationen';
import { useTree } from '@/hooks/useTree';
import { AppLayout } from '@/components/Layout/AppLayout';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const TOPBAR_BASE = 56; // h-14

/** Topbar-Höhe inkl. iOS Safe-Area (Notch) — env(safe-area-inset-top) über ein
 *  temporäres Mess-Element auflösen (CSS custom properties resolven env() nicht). */
function readTopbarHeight(): number {
  if (typeof window === 'undefined' || typeof document === 'undefined') return TOPBAR_BASE;
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;top:0;left:0;width:0;height:0;padding-top:env(safe-area-inset-top);visibility:hidden;pointer-events:none;';
  document.body.appendChild(probe);
  const inset = parseFloat(getComputedStyle(probe).paddingTop) || 0;
  probe.remove();
  return TOPBAR_BASE + inset;
}

function parseBirthYear(text: string | null): number | null {
  if (!text) return null;
  const match = text.match(/\b(1[4-9][0-9]{2}|20[0-2][0-9])\b/);
  return match ? parseInt(match[1], 10) : null;
}

function countDescendants(nodeId: string, childrenMap: Map<string, string[]>): number {
  const visited = new Set<string>();
  const queue = [nodeId];
  let count = 0;
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const child of childrenMap.get(current) || []) {
      if (!visited.has(child)) {
        visited.add(child);
        count++;
        queue.push(child);
      }
    }
  }
  return count;
}

function getAncestors(nodeId: string, parentMap: Map<string, string[]>): Set<string> {
  const ancestors = new Set<string>();
  const queue = [nodeId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const parent of parentMap.get(current) || []) {
      if (!ancestors.has(parent)) {
        ancestors.add(parent);
        queue.push(parent);
      }
    }
  }
  return ancestors;
}

// ─── Sortierung der Tiefenachse ──────────────────────────────────────────────
//
// Die Z-Achse ordnet die Personen von alt (hinten) nach jung (vorn). Wonach
// dabei sortiert wird, ist keine Kleinigkeit:
//
//   * Nach Generation – wer wessen Kind ist. Eltern liegen immer hinter ihren
//     Kindern, ausnahmslos, auch wenn kein einziges Geburtsdatum gepflegt ist.
//     Das ist die Ordnung, die ein Stammbaum eigentlich meint.
//   * Nach Geburtsjahr – die tatsächliche Zeitachse. Ehrlicher zu den Daten,
//     aber lückenhaft: wer kein Datum hat, muss geschätzt werden.
//
// Beides hat seine Berechtigung, deshalb ist es umschaltbar. Vorgabe ist die
// Generation, weil sie ohne gepflegte Daten auskommt.

// ─── CosmosPage ───────────────────────────────────────────────────────────────

export function CosmosPage() {
  const { data: treeData, isLoading, isError } = useTree();
  const graphRef = useRef<any>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [sortierung, setSortierung] = useState<Sortierung>('generation');
  const [zeigeNamen, setZeigeNamen] = useState(true);
  const [infoOffen, setInfoOffen] = useState(false);
  const [dims, setDims] = useState({
    width: window.innerWidth,
    height: window.innerHeight - readTopbarHeight(),
  });

  // Am Handy ist der Platz knapp und der Finger ungenau: Legende und
  // Erläuterungen verstecken sich hinter einem Knopf, Namen werden nur für
  // die großen Sterne gezeichnet.
  const schmal = dims.width < 640;

  // ── Window resize ─────────────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () =>
      setDims({ width: window.innerWidth, height: window.innerHeight - readTopbarHeight() });
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  // ── Parent / children maps ────────────────────────────────────────────────
  const parentMap = useMemo(() => {
    const map = new Map<string, string[]>();
    if (!treeData) return map;
    for (const node of treeData.nodes) {
      const parents: string[] = [];
      if (node.primaryParentId) parents.push(node.primaryParentId);
      if (node.secondaryParentId) parents.push(node.secondaryParentId);
      if (parents.length > 0) map.set(node.id, parents);
    }
    return map;
  }, [treeData]);

  const childrenMap = useMemo(() => {
    const map = new Map<string, string[]>();
    if (!treeData) return map;
    for (const node of treeData.nodes) {
      if (node.primaryParentId) {
        if (!map.has(node.primaryParentId)) map.set(node.primaryParentId, []);
        map.get(node.primaryParentId)!.push(node.id);
      }
      if (node.secondaryParentId) {
        if (!map.has(node.secondaryParentId)) map.set(node.secondaryParentId, []);
        map.get(node.secondaryParentId)!.push(node.id);
      }
    }
    return map;
  }, [treeData]);

  // ── Generationen ──────────────────────────────────────────────────────────
  const generationen = useMemo(
    () => (treeData ? berechneGenerationen(treeData.nodes.map((n) => n.id), parentMap) : new Map()),
    [treeData, parentMap],
  );

  // ── Graph data ────────────────────────────────────────────────────────────
  const graphData = useMemo(() => {
    if (!treeData) return { nodes: [], links: [], generationen: 0 };

    // Gültige Node-IDs → dangling links (Verweise auf fehlende Personen) filtern,
    // sonst zeichnet der Force-Graph Linien ins Leere und wirkt „zerrissen".
    const validIds = new Set(treeData.nodes.map((n) => n.id));

    const tiefe = (id: string): number => (generationen.get(id) as number | undefined) ?? 0;

    // Wo jede Person auf der Tiefenachse sitzt – die Regeln dazu stehen in
    // utils/generationen.ts und sind dort geprüft.
    const lagen = tiefenAchse(
      treeData.nodes.map((n) => ({ id: n.id, jahr: parseBirthYear(n.birthDateText) })),
      generationen as Map<string, number>,
      sortierung,
    );

    const nodes = treeData.nodes.map((node) => {
      const birthYear = parseBirthYear(node.birthDateText);
      const generation = tiefe(node.id);
      const descendants = countDescendants(node.id, childrenMap);
      // Larger node = more descendants + bonus for profile photo
      const nodeVal = 3 + Math.min(descendants, 30) * 0.5 + (node.profileMediaId ? 2 : 0);

      // Zeit-Achse fixieren: alte Generationen hinten (negatives z),
      // junge Generationen vorne (positives z). Kamera blickt auf +z,
      // daher ist höheres z = näher am Betrachter = „vorne".
      // fz pinnt die Z-Koordinate, sodass das Force-Layout sie nicht verschiebt.
      const z = lagen.get(node.id)?.z ?? 0;

      return {
        id: node.id,
        name: [node.firstName, node.lastName].filter(Boolean).join(' '),
        gender: node.gender,
        birthYear,
        generation,
        // Geschätzt heißt: nicht gepflegt. Das wird auch angezeigt, damit
        // niemand eine Position für eine Angabe hält.
        geschaetzt: birthYear == null,
        z,
        fz: z,
        nodeVal,
      };
    });

    const links: { source: string; target: string; linkType: string }[] = [];
    const seen = new Set<string>();
    const addLink = (source: string, target: string, linkType: string) => {
      if (!validIds.has(source) || !validIds.has(target) || source === target) return;
      // Duplikate (gleiches Paar) vermeiden, damit Partnerschaften nicht doppelt gezeichnet werden
      const key = source < target ? `${source}|${target}|${linkType}` : `${target}|${source}|${linkType}`;
      if (seen.has(key)) return;
      seen.add(key);
      links.push({ source, target, linkType });
    };

    // Parent → child edges (carry directional particles)
    for (const node of treeData.nodes) {
      if (node.primaryParentId) addLink(node.primaryParentId, node.id, 'parent-child');
      if (node.secondaryParentId) addLink(node.secondaryParentId, node.id, 'parent-child');
    }
    // Partnership edges
    for (const link of treeData.links) {
      addLink(link.person1Id, link.person2Id, link.type || 'partnership');
    }

    const maxGen = treeData.nodes.reduce((m, n) => Math.max(m, tiefe(n.id)), 0);
    return { nodes, links, generationen: maxGen + 1 };
  }, [treeData, childrenMap, generationen, sortierung]);

  // ── Ancestor highlight set ────────────────────────────────────────────────
  const ancestorSet = useMemo(
    () => (selectedNodeId ? getAncestors(selectedNodeId, parentMap) : new Set<string>()),
    [selectedNodeId, parentMap]
  );

  // ── Color accessors ───────────────────────────────────────────────────────
  const getNodeColor = useCallback(
    (node: any): string => {
      if (selectedNodeId) {
        if (node.id === selectedNodeId) return '#f97316'; // orange – selected
        if (ancestorSet.has(node.id)) return '#fbbf24';  // amber  – ancestor
        return '#1e293b';                                  // dimmed
      }
      if (node.gender === 'male') return '#60a5fa';
      if (node.gender === 'female') return '#f472b6';
      return '#94a3b8';
    },
    [selectedNodeId, ancestorSet]
  );

  const getLinkColor = useCallback(
    (link: any): string => {
      const srcId = typeof link.source === 'object' ? link.source.id : link.source;
      const tgtId = typeof link.target === 'object' ? link.target.id : link.target;

      if (selectedNodeId) {
        const srcHighlighted = srcId === selectedNodeId || ancestorSet.has(srcId);
        const tgtHighlighted = tgtId === selectedNodeId || ancestorSet.has(tgtId);
        if (srcHighlighted && tgtHighlighted && link.linkType === 'parent-child')
          return '#fbbf24';
        // Etwas sichtbarer als vorher (0.03 → 0.08), damit der Graph nicht „auseinanderfällt"
        return 'rgba(255,255,255,0.08)';
      }
      if (link.linkType === 'parent-child') return 'rgba(148,197,255,0.55)';
      return 'rgba(244,114,182,0.55)';
    },
    [selectedNodeId, ancestorSet]
  );

  const getLinkWidth = useCallback(
    (link: any): number => (link.linkType === 'parent-child' ? 1.2 : 0.9),
    []
  );

  // Partnerschaften leicht gebogen → kein Überlappen mit Eltern-Kind-Kanten
  const getLinkCurvature = useCallback(
    (link: any): number => (link.linkType === 'parent-child' ? 0 : 0.25),
    []
  );

  const getParticleCount = useCallback((link: any): number => {
    // Partikel auch auf Partnerschaften, nur langsamer/weniger
    return link.linkType === 'parent-child' ? 2 : 1;
  }, []);

  const getParticleSpeed = useCallback(
    (link: any): number => (link.linkType === 'parent-child' ? 0.006 : 0.002),
    []
  );

  const getParticleColor = useCallback(
    (link: any): string => {
      if (!selectedNodeId) {
        return link.linkType === 'parent-child' ? '#93c5fd' : '#f9a8d4';
      }
      const srcId = typeof link.source === 'object' ? link.source.id : link.source;
      const tgtId = typeof link.target === 'object' ? link.target.id : link.target;
      const highlighted =
        (srcId === selectedNodeId || ancestorSet.has(srcId)) &&
        (tgtId === selectedNodeId || ancestorSet.has(tgtId));
      return highlighted ? '#fbbf24' : 'rgba(255,255,255,0.1)';
    },
    [selectedNodeId, ancestorSet]
  );

  // ── Namens-Labels ─────────────────────────────────────────────────────────
  // Für jeden Stern erzeugen wir eine Sphere + optional einen SpriteText mit
  // dem Namen. nodeThreeObjectExtend=false, weil wir die Sphere selbst
  // erstellen, um Größe und Label gemeinsam zu steuern.
  const nodeThreeObject = useCallback(
    (node: any) => {
      const group = new THREE.Group();

      // Stern (Sphere)
      const radius = Math.max(1.5, Math.sqrt(node.nodeVal ?? 3) * 1.4);
      const sphereGeo = new THREE.SphereGeometry(radius, 12, 10);
      const sphereMat = new THREE.MeshBasicMaterial({
        color: getNodeColor(node),
        transparent: true,
        opacity: 0.9,
      });
      const sphere = new THREE.Mesh(sphereGeo, sphereMat);
      group.add(sphere);

      const isSelected = selectedNodeId === node.id;
      const isAncestor = selectedNodeId ? ancestorSet.has(node.id) : false;

      // Am Handy alle Namen zu zeichnen ergibt eine unlesbare Textwand. Dort
      // stehen nur die großen Sterne (viele Nachkommen) und das an, was gerade
      // hervorgehoben ist.
      const wichtig = isSelected || isAncestor || (node.nodeVal ?? 0) >= 6;
      const beschriften = zeigeNamen && node.name && (!schmal || wichtig);

      if (beschriften) {
        const dimmed = selectedNodeId && !isSelected && !isAncestor;

        const sprite = new SpriteText(node.name);
        sprite.color = isSelected
          ? '#fbbf24'
          : isAncestor
            ? '#fde68a'
            : dimmed
              ? 'rgba(200,200,220,0.35)'
              : '#e2e8f0';
        sprite.textHeight = isSelected ? 5 : schmal ? 4.5 : 3.5;
        sprite.fontWeight = isSelected || isAncestor ? '600' : '400';
        sprite.backgroundColor = dimmed
          ? 'rgba(0,0,0,0)'
          : 'rgba(0,0,0,0.55)';
        sprite.padding = 1.5;
        sprite.borderRadius = 2;
        // Label leicht über den Stern versetzen.
        // Cast: three-spritetext erbt von THREE.Sprite, aber die Types
        // exponieren `position` nicht direkt.
        (sprite as unknown as THREE.Object3D).position.set(0, radius + 3, 0);
        group.add(sprite as unknown as THREE.Object3D);
      }

      return group;
    },
    [getNodeColor, selectedNodeId, ancestorSet, zeigeNamen, schmal]
  );

  // ── Kamerasteuerung ───────────────────────────────────────────────────────
  //
  // Am Rechner genügen Maus und Rad. Am Handy nicht: Zwei-Finger-Zoom trifft
  // man auf einem drehbaren Graphen selten, und „ich sehe nichts mehr" ist
  // ohne Rückweg eine Sackgasse. Deshalb echte Knöpfe – groß genug für einen
  // Daumen.

  /** Kamera zum Blickpunkt hin oder von ihm weg bewegen. */
  const zoome = useCallback((faktor: number) => {
    const g = graphRef.current;
    if (!g) return;
    const pos = g.cameraPosition();
    const ziel = g.controls?.()?.target ?? { x: 0, y: 0, z: 0 };
    g.cameraPosition(
      {
        x: ziel.x + (pos.x - ziel.x) * faktor,
        y: ziel.y + (pos.y - ziel.y) * faktor,
        z: ziel.z + (pos.z - ziel.z) * faktor,
      },
      ziel,
      250,
    );
  }, []);

  /**
   * Kamera so setzen, dass alle Sterne im Bild sind – und die Generationen
   * auch als Generationen zu erkennen.
   *
   * Zwei Dinge stecken darin, die beide nötig sind:
   *
   *   1. Der Blick geht SCHRÄG auf die Tiefenachse. Von vorn betrachtet liegen
   *      alle Generationen genau hintereinander; gemessen ergaben die zehn
   *      Personen eines Testbaums eine Wolke von 32 × 32 × 210 Einheiten – ein
   *      Stab, den die Kamera der Länge nach ansah. Auf dem Schirm blieb davon
   *      ein Fleck von 60 Pixeln. Die ganze Sortierung nützt nichts, wenn man
   *      sie nicht sieht.
   *   2. Der Abstand kommt aus der halben Raumdiagonale des Hüllquaders. Das
   *      zoomToFit der Bibliothek rechnete hier um ein Vielfaches daneben.
   */
  const zeigeAlles = useCallback(() => {
    const g = graphRef.current;
    const knoten = graphData.nodes as { x?: number; y?: number; z?: number }[];
    if (!g || knoten.length === 0) return;

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const n of knoten) {
      const x = n.x ?? 0;
      const y = n.y ?? 0;
      const z = n.z ?? 0;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      if (z < minZ) minZ = z;
      if (z > maxZ) maxZ = z;
    }
    const mitte = { x: (minX + maxX) / 2, y: (minY + maxY) / 2, z: (minZ + maxZ) / 2 };

    // Untergrenze für den Fall eines einzigen Sterns, sonst stünde die Kamera
    // in ihm drin. Der Zuschlag ist Platz für die Namensschilder – am Handy
    // mehr, weil die Schrift dort im Verhältnis größer ist.
    const halbeDiagonale =
      0.5 * Math.hypot(maxX - minX, maxY - minY, maxZ - minZ) || 0;
    const radius = Math.max(halbeDiagonale, 60) * (schmal ? 1.45 : 1.35);

    const BLICKWINKEL_GRAD = 50; // senkrecht, Vorgabe der Bibliothek
    const senkrecht = Math.tan((BLICKWINKEL_GRAD * Math.PI) / 360);
    // Im Hochformat ist der WAAGERECHTE Blickwinkel der engere – ein Fenster
    // von 375 × 756 sieht seitlich nur halb so viel wie nach oben und unten.
    // Ohne diesen Faktor ragt der Baum am Handy links und rechts aus dem Bild.
    const seitenverhaeltnis = dims.height > 0 ? dims.width / dims.height : 1;
    const abstand = radius / (senkrecht * Math.min(1, seitenverhaeltnis));

    // Von schräg rechts oben und vorn. Die Tiefenachse läuft damit sichtbar
    // durchs Bild, statt auf einen Punkt zusammenzufallen.
    const RICHTUNG = { x: 0.62, y: 0.35, z: 0.70 }; // bereits normiert
    g.cameraPosition(
      {
        x: mitte.x + RICHTUNG.x * abstand,
        y: mitte.y + RICHTUNG.y * abstand,
        z: mitte.z + RICHTUNG.z * abstand,
      },
      mitte,
      600,
    );
  }, [graphData, schmal, dims]);

  // Nach dem Laden und nach jedem Wechsel der Sortierung einmal alles
  // einpassen. Ohne das steht man vor einem Fleck in der Bildmitte und muss
  // erst suchen, wo der Baum hin ist.
  //
  // Zwei Auslöser, weil keiner allein genügt:
  //
  //   * Ein kurzer Zeitgeber. Die Aufwärm-Schritte des Layouts laufen
  //     synchron, die Koordinaten stehen also schon kurz nach dem Aufbau fest.
  //   * onEngineStop, wenn die Simulation ausläuft – dann sitzen die Sterne
  //     endgültig, und bei großen Bäumen dauert das länger als der Zeitgeber.
  //
  // Beide rufen dieselbe Funktion; ein zweites Einpassen auf unveränderte
  // Koordinaten ist ein Nullschritt.
  useEffect(() => {
    if (graphData.nodes.length === 0) return;
    const t = window.setTimeout(zeigeAlles, 350);
    return () => window.clearTimeout(t);
  }, [graphData, zeigeAlles]);

  // Nur solange der Betrachter die Kamera noch nicht selbst angefasst hat –
  // sonst risse ihm ein spät auslaufendes Layout die Ansicht weg.
  const selbstGesteuert = useRef(false);
  useEffect(() => {
    selbstGesteuert.current = false;
  }, [graphData]);

  const layoutFertig = useCallback(() => {
    if (selbstGesteuert.current) return;
    zeigeAlles();
  }, [zeigeAlles]);

  // ── Interaction ───────────────────────────────────────────────────────────
  const handleNodeClick = useCallback((node: any) => {
    // Toggle: click same node again to deselect
    setSelectedNodeId((prev) => (prev === node.id ? null : node.id));

    const { x = 0, y = 0, z = 0 } = node;
    const len = Math.hypot(x, y, z) || 1;
    const distance = 200;
    graphRef.current?.cameraPosition(
      {
        x: x + (x / len) * distance,
        y: y + (y / len) * distance,
        z: z + (z / len) * distance,
      },
      node,
      1500
    );
  }, []);

  const handleBackgroundClick = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  // ── Render states ─────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex h-full items-center justify-center bg-black">
          <div className="text-center text-white">
            <div className="text-5xl mb-4">✨</div>
            <p className="text-slate-300 text-sm">Sternenhimmel wird geladen…</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (isError || !treeData) {
    return (
      <AppLayout>
        <div className="flex h-full items-center justify-center bg-black">
          <p className="text-red-400 text-sm">Fehler beim Laden der Baum-Daten.</p>
        </div>
      </AppLayout>
    );
  }

  const selectedNode = selectedNodeId
    ? graphData.nodes.find((n) => n.id === selectedNodeId)
    : null;

  const knopf =
    'flex h-11 w-11 items-center justify-center rounded-xl text-slate-200 transition ' +
    'hover:bg-white/10 active:bg-white/20 disabled:opacity-40';

  return (
    <AppLayout>
      <div
        className="relative overflow-hidden bg-black"
        style={{ width: dims.width, height: dims.height }}
        // Sobald jemand selbst dreht oder zoomt, hört das automatische
        // Einpassen auf – niemand mag es, wenn ihm die Ansicht unter der Hand
        // weggezogen wird.
        onPointerDown={() => {
          selbstGesteuert.current = true;
        }}
      >

        {/* ── Info overlay (top-left) ──────────────────────────────────────── */}
        <div className="absolute left-2 top-2 z-10 max-w-[70%] select-none rounded-xl bg-black/60 px-3 py-2 text-white backdrop-blur-sm [pointer-events:none] sm:left-4 sm:top-4 sm:px-4 sm:py-3">
          <h1 className="text-base font-semibold tracking-wide">✨ Sternenhimmel</h1>
          <p className="text-xs text-slate-400 mt-0.5">
            {graphData.nodes.length} Personen · {graphData.generationen} Generationen
          </p>
          {selectedNode && (
            <div className="mt-2 border-t border-white/10 pt-2">
              <p className="text-xs font-medium text-amber-400">{selectedNode.name}</p>
              <p className="text-[11px] text-slate-400">
                {selectedNode.birthYear
                  ? `geb. ${selectedNode.birthYear}`
                  : 'Geburtsjahr unbekannt'}
                {' · '}
                {selectedNode.generation + 1}. Generation
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5">{ancestorSet.size} Vorfahren</p>
            </div>
          )}
        </div>

        {/* ── Legende ──────────────────────────────────────────────────────── */}
        {/* Am Handy kostet ein dauerhaft eingeblendeter Kasten zu viel Bild –
            dort steckt er hinter dem ℹ-Knopf in der Leiste. */}
        {(!schmal || infoOffen) && (
          <div
            className={
              schmal
                ? 'absolute inset-x-2 bottom-20 z-20 select-none rounded-xl bg-black/85 px-4 py-3 text-xs text-white backdrop-blur-sm'
                : 'absolute bottom-4 right-4 z-10 select-none rounded-xl bg-black/60 px-4 py-3 text-xs text-white backdrop-blur-sm [pointer-events:none]'
            }
          >
            {schmal && (
              <button
                onClick={() => setInfoOffen(false)}
                aria-label="Legende schließen"
                className="absolute right-1.5 top-1.5 flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-white/10"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
            <div className="flex items-center gap-2 mb-1.5">
              <span className="inline-block w-3 h-3 rounded-full bg-blue-400" />
              <span className="text-slate-300">Männlich</span>
            </div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="inline-block w-3 h-3 rounded-full bg-pink-400" />
              <span className="text-slate-300">Weiblich</span>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-block w-3 h-3 rounded-full bg-slate-400" />
              <span className="text-slate-300">Unbekannt</span>
            </div>
            <div className="border-t border-white/10 pt-2 space-y-0.5 text-slate-500 text-[10px]">
              <p>Tippen zum Fokussieren</p>
              <p>Alt = hinten · Jung = vorn</p>
              <p>
                Tiefe:{' '}
                {sortierung === 'generation'
                  ? 'nach Generation'
                  : 'nach Geburtsjahr (fehlende geschätzt)'}
              </p>
              <p>Partikel = Ahnen-Fluss</p>
            </div>
          </div>
        )}

        {/* ── Steuerleiste ─────────────────────────────────────────────────── */}
        <div className="absolute inset-x-0 bottom-0 z-20 flex justify-center pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:justify-start sm:pl-4">
          <div className="flex items-center gap-1 rounded-2xl bg-black/70 p-1 backdrop-blur-sm">
            <button
              onClick={() => zoome(0.7)}
              aria-label="Näher heran"
              className={knopf}
            >
              <ZoomIn className="h-5 w-5" aria-hidden="true" />
            </button>
            <button
              onClick={() => zoome(1 / 0.7)}
              aria-label="Weiter weg"
              className={knopf}
            >
              <ZoomOut className="h-5 w-5" aria-hidden="true" />
            </button>
            <button onClick={zeigeAlles} aria-label="Alles zeigen" className={knopf}>
              <Maximize2 className="h-5 w-5" aria-hidden="true" />
            </button>

            <span className="mx-1 h-6 w-px bg-white/15" />

            <button
              onClick={() =>
                setSortierung((s) => (s === 'generation' ? 'jahr' : 'generation'))
              }
              aria-label={
                sortierung === 'generation'
                  ? 'Nach Geburtsjahr anordnen'
                  : 'Nach Generation anordnen'
              }
              aria-pressed={sortierung === 'jahr'}
              className={knopf}
            >
              {sortierung === 'generation' ? (
                <Layers className="h-5 w-5" aria-hidden="true" />
              ) : (
                <CalendarRange className="h-5 w-5 text-amber-400" aria-hidden="true" />
              )}
            </button>
            <button
              onClick={() => setZeigeNamen((z) => !z)}
              aria-label={zeigeNamen ? 'Namen ausblenden' : 'Namen einblenden'}
              aria-pressed={zeigeNamen}
              className={knopf}
            >
              <Tag
                className={`h-5 w-5 ${zeigeNamen ? '' : 'text-slate-500'}`}
                aria-hidden="true"
              />
            </button>

            {schmal && (
              <button
                onClick={() => setInfoOffen((o) => !o)}
                aria-label="Legende"
                aria-pressed={infoOffen}
                className={knopf}
              >
                <Info className="h-5 w-5" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        {/* ── 3D Force Graph ───────────────────────────────────────────────── */}
        <ForceGraph3D
          ref={graphRef}
          graphData={graphData}
          width={dims.width}
          height={dims.height}
          backgroundColor="#000008"
          // Custom node: Stern + Namens-Label
          nodeThreeObject={nodeThreeObject}
          nodeThreeObjectExtend={false}
          nodeLabel={() => ''}
          nodeVal={(node: any) => node.nodeVal}
          // Am Touchscreen ist der Finger deutlich ungenauer als der Mauszeiger.
          nodeRelSize={schmal ? 6 : 4}
          // Link appearance
          linkColor={getLinkColor}
          linkOpacity={0.85}
          linkWidth={getLinkWidth}
          linkCurvature={getLinkCurvature}
          // Particle animation (Ahnen-Fluss: Vergangenheit → Gegenwart; Paare beidseitig)
          linkDirectionalParticles={getParticleCount}
          linkDirectionalParticleSpeed={getParticleSpeed}
          linkDirectionalParticleWidth={1.8}
          linkDirectionalParticleColor={getParticleColor}
          // Interaction
          onNodeClick={handleNodeClick}
          onBackgroundClick={handleBackgroundClick}
          onEngineStop={layoutFertig}
        />
      </div>
    </AppLayout>
  );
}
